// src/pages/MartyrsPlayers.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@/components/PageHero';
import { MemorialCard } from '@/components/MemorialCard';
import { Button } from '@/components/ui/button';
import { 
  martyrsPlayers, 
  getPlayersBySport,
  playersStatistics 
} from '@/data/mock/martyrsPlayers';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Star,
  Trophy,
  Sparkles,
  Users,
  Target,
  Flame,
  Sunrise,
  Search,
  Filter,
  X,
  Calendar,
  Award,
  Flag,
  Quote,
  Baby,
  User,
  UserCheck,
  BookOpen,
  MessageCircle
} from 'lucide-react';

const MartyrsPlayers = ({ embedded = false }: { embedded?: boolean }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [selectedSport, setSelectedSport] = useState<string>('all');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Get unique sports for filtering
  const sports = useMemo(() => {
    const uniqueSports = Array.from(new Set(martyrsPlayers.map(p => p.sport.en)));
    return ['all', ...uniqueSports];
  }, []);

  // Filter players
  const filteredPlayers = useMemo(() => {
    return martyrsPlayers.filter(player => {
      const matchesSport = selectedSport === 'all' || player.sport.en === selectedSport;
      
      const matchesAgeGroup = selectedAgeGroup === 'all' || (() => {
        if (!player.age) return false;
        switch (selectedAgeGroup) {
          case 'children': return player.age < 13;
          case 'teens': return player.age >= 13 && player.age < 18;
          case 'youngAdults': return player.age >= 18;
          default: return true;
        }
      })();
      
      const matchesSearch = searchQuery === '' || 
        player.name.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player.name.ar.includes(searchQuery);
      
      return matchesSport && matchesAgeGroup && matchesSearch;
    });
  }, [selectedSport, selectedAgeGroup, searchQuery]);

  // Statistics
  const stats = [
    {
      icon: Heart,
      value: martyrsPlayers.length.toString(),
      label: { en: 'Young Stars Lost', ar: 'نجوم شباب فقدناهم' },
      color: 'from-red-500 to-pink-500'
    },
    {
      icon: Baby,
      value: playersStatistics.byAgeGroup.children.toString(),
      label: { en: 'Children', ar: 'أطفال' },
      color: 'from-blue-500 to-cyan-500',
      description: { en: 'Under 13', ar: 'أقل من 13' }
    },
    {
      icon: User,
      value: playersStatistics.byAgeGroup.teens.toString(),
      label: { en: 'Teens', ar: 'مراهقون' },
      color: 'from-purple-500 to-pink-500',
      description: { en: '13-17 years', ar: '13-17 سنة' }
    },
    {
      icon: Star,
      value: '∞',
      label: { en: 'Dreams Living On', ar: 'أحلام تعيش' },
      color: 'from-yellow-500 to-orange-500'
    },
  ];

  // Dreams Section
  const dreamsHighlight = martyrsPlayers
    .filter(p => p.dream)
    .slice(0, 3);

  // Memorial quotes
  const memorialMessages = [
    {
      text: {
        en: 'They were not just players, they were dreamers. Every game they played was a step toward their aspirations.',
        ar: 'لم يكونوا مجرد لاعبين، بل كانوا حالمين. كل مباراة لعبوها كانت خطوة نحو طموحاتهم.'
      }
    },
    {
      text: {
        en: 'Their laughter on the field, their determination in training, their dreams of greatness - all live on in our hearts.',
        ar: 'ضحكتهم في الملعب، عزيمتهم في التدريب، أحلامهم بالعظمة - كلها تعيش في قلوبنا.'
      }
    }
  ];

  return (
    <div className={embedded ? '' : 'min-h-screen'}>
      {/* Enhanced Page Hero */}
      {!embedded && (
        <PageHero
          title={t('martyrs.playersTitle')}
          subtitle={isRTL
            ? 'نتذكر لاعبينا الشباب الذين حلموا بمستقبل مشرق - ضحكتهم، طاقتهم، وشغفهم سيبقى معنا إلى الأبد'
            : 'Remembering our young players who dreamed of a bright future - their laughter, energy, and passion remain with us forever'
          }
          breadcrumbs={[
            { label: t('common.home'), path: '/' },
            { label: t('nav.martyrsPlayers') }
          ]}
        />
      )}

      {/* Memorial Banner */}
      <section className="py-6 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border-y border-primary/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-3 text-center flex-wrap"
          >
            <Star className="h-6 w-6 text-yellow-500" />
            <p className="text-sm font-semibold text-foreground">
              {isRTL
                ? 'نجوم شابة أضاءت الملاعب - ذكراهم نور لا ينطفئ'
                : 'Young stars who lit up the fields - their memory is an eternal light'
              }
            </p>
            <Star className="h-6 w-6 text-yellow-500" />
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            {/* Memorial Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", duration: 0.8 }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 p-1 mb-8 shadow-2xl shadow-yellow-500/30"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <Star className="h-12 w-12 text-yellow-500" />
              </div>
            </motion.div>

            {/* Text */}
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              {isRTL
                ? 'نتذكر لاعبينا الشباب الذين حلموا بمستقبل مشرق في الرياضة. ضحكتهم وطاقتهم وشغفهم باللعب ستبقى معنا إلى الأبد.'
                : 'We remember our young players who dreamed of a bright future in sports. Their laughter, energy, and passion for the game will remain with us forever.'}
            </p>

            {/* Forever Badge */}
            <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-yellow-500/10 border-2 border-yellow-500/30 shadow-lg">
              <Sparkles className="h-6 w-6 text-yellow-500" />
              <span className="text-yellow-500 font-bold text-lg">{t('martyrs.forever')}</span>
              <Sparkles className="h-6 w-6 text-yellow-500" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Trophy className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'إحصائيات' : 'Statistics'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'نجوم لا تُنسى' : 'Unforgettable Stars'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="p-8 rounded-3xl bg-card border-2 border-border hover:border-yellow-500/30 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-4`}>
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium mb-1">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
                {stat.description && (
                  <div className="text-xs text-muted-foreground/70">
                    {stat.description[isRTL ? 'ar' : 'en']}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Their Dreams Section */}
      {dreamsHighlight.length > 0 && (
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Target className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'أحلامهم' : 'Their Dreams'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'الأحلام التي عاشوا من أجلها' : 'The Dreams They Lived For'}
            </h2>

            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'كل لاعب كان لديه حلم، هدف، وطموح. نحن نحمل هذه الأحلام ونعمل على تحقيقها'
                : 'Every player had a dream, a goal, an aspiration. We carry these dreams and work to fulfill them'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {dreamsHighlight.map((player, idx) => (
              <motion.div
                key={player.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="p-8 rounded-3xl bg-card border-2 border-border hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 relative overflow-hidden group"
              >
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-yellow-500/10 border-2 border-yellow-500/30 flex items-center justify-center mb-6 mx-auto">
                    <Target className="h-8 w-8 text-yellow-500" />
                  </div>

                  <h3 className="text-xl font-bold mb-4 text-center">
                    {player.name[isRTL ? 'ar' : 'en']}
                  </h3>

                  <div className="flex items-center justify-center gap-2 mb-6">
                    <Trophy className="h-4 w-4 text-primary" />
                    <span className="text-sm text-primary font-medium">
                      {player.sport[isRTL ? 'ar' : 'en']}
                    </span>
                  </div>

                  {player.dream && (
                    <div className="p-6 rounded-2xl bg-yellow-500/5 border border-yellow-500/20">
                      <Quote className="h-6 w-6 text-yellow-500/50 mb-3" />
                      <p className="text-foreground italic leading-relaxed text-center">
                        "{player.dream[isRTL ? 'ar' : 'en']}"
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Memorial Messages */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            {memorialMessages.map((message, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="p-10 rounded-3xl bg-card border-2 border-border shadow-xl relative"
              >
                <BookOpen className="h-12 w-12 text-primary/20 absolute top-6 right-6" />
                <div className="relative">
                  <p className="text-2xl text-foreground font-medium leading-relaxed italic">
                    "{message.text[isRTL ? 'ar' : 'en']}"
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1 top-20 z-40 border-b border-border shadow-lg">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
                <input
                  type="text"
                  placeholder={isRTL ? 'ابحث عن اسم...' : 'Search by name...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full ${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none transition-all`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-all`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Sport Filter */}
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <select
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="px-6 py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none cursor-pointer transition-all font-medium min-w-[150px]"
                >
                  <option value="all">{isRTL ? 'جميع الرياضات' : 'All Sports'}</option>
                  {sports.slice(1).map(sport => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>
              </div>

              {/* Age Group Filter */}
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <select
                  value={selectedAgeGroup}
                  onChange={(e) => setSelectedAgeGroup(e.target.value)}
                  className="px-6 py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none cursor-pointer transition-all font-medium min-w-[150px]"
                >
                  <option value="all">{isRTL ? 'جميع الأعمار' : 'All Ages'}</option>
                  <option value="children">{isRTL ? 'أطفال (<13)' : 'Children (<13)'}</option>
                  <option value="teens">{isRTL ? 'مراهقون (13-17)' : 'Teens (13-17)'}</option>
                  <option value="youngAdults">{isRTL ? 'شباب (18+)' : 'Young Adults (18+)'}</option>
                </select>
              </div>
            </div>

            {/* Results Count */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center"
            >
              <span className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredPlayers.length} من ${martyrsPlayers.length}`
                  : `Showing ${filteredPlayers.length} of ${martyrsPlayers.length}`
                }
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Memorial Cards Grid */}
      <section className="py-20 bg-gradient-to-b from-surface-1 to-background">
        <div className="container mx-auto px-4">
          {filteredPlayers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPlayers.map((player, idx) => (
                <MemorialCard
                  key={player.id}
                  name={player.name[isRTL ? 'ar' : 'en']}
                  role={player.sport[isRTL ? 'ar' : 'en']}
                  years={player.years}
                  image={player.image}
                  bio={player.bio?.[isRTL ? 'ar' : 'en']}
                  achievements={player.achievements?.[isRTL ? 'ar' : 'en']}
                  quote={player.favoriteQuote?.[isRTL ? 'ar' : 'en']}
                  dateOfPassing={player.dateOfPassing}
                  delay={idx * 0.1}
                />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground">
                {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Legacy Message */}
      <section className="py-20 bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-yellow-500/30 via-orange-500/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Icon */}
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0],
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 p-1 mb-8 shadow-2xl"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <Sunrise className="h-12 w-12 text-yellow-500" />
              </div>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'أحلامهم تعيش من خلالنا' : 'Their Dreams Live Through Us'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              {isRTL
                ? 'كل مباراة نلعبها، كل بطولة نفوز بها، كل طفل نُلهمه - هو تكريم لذكراهم وأحلامهم'
                : 'Every game we play, every championship we win, every child we inspire - is a tribute to their memory and dreams'
              }
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-12 py-7 text-lg rounded-full btn-glow">
                <BookOpen className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'كتاب الذكريات' : 'Memory Book'}
              </Button>

              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <MessageCircle className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'شارك قصة' : 'Share a Story'}
              </Button>
            </div>

            {/* Eternal Symbol */}
            <motion.div
              animate={{
                opacity: [0.5, 1, 0.5],
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="mt-16 pt-16 border-t border-border"
            >
              <div className="flex items-center justify-center gap-4">
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-yellow-500" />
                <Star className="h-12 w-12 text-yellow-500" />
                <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-yellow-500" />
              </div>
              <p className="text-sm text-muted-foreground mt-4 font-medium">
                {isRTL ? 'نجوم خالدة في سماء الرياضة' : 'Eternal Stars in the Sky of Sports'}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MartyrsPlayers;
